"use client";

import React, { startTransition, useEffect, useState } from "react";
import { getUniversitySettings } from "@/api/frontend";
import type { IUniversitySettings } from "@/api/admin/universitySettings";
import { IUniversityDetails } from "@/types/university";
import type { ICourse } from "@/api/admin/course";
import { IQualification } from "@/store/slices/leaddrop";
import { ILeaddropFormData } from "@/store/slices/leaddrop";
import OpenLeadDrop from "./OpenLeadDrop";
import { useResolvedLeadForm } from "@/components/organisms/leadForm/useResolvedLeadForm";
import {
  handleSetLeaddropFormData,
  setLeadDropPopupUiVariant,
} from "@/store/slices/leaddrop";
import { useDispatch } from "react-redux";
import {
  setIsLeadSubmittedSuccessfully,
  setUniversity,
  setPromotionalBanner,
  setFloatingIcons,
  setPromotionalBannerOpen,
} from "@/store/slices/globalslice";
import { useDelayForm } from "@/hooks/useDelayForm";
import { hasSeenPromotionalBanner } from "@/sessionStorage";
import {
  setLeaddropFormData,
  setLeaddropFormVariant,
} from "@/store/slices/leaddropform";
import type { AppDispatch } from "@/store/store";
import { UNIVERSITY_SETTINGS_SECTION_TYPES } from "../../../constants";

interface UniversitySettingsResponse {
  data?: {
    settings?: IUniversitySettings[];
    lead_drop_popup?: unknown;
    lead_drop_form?: unknown;
    courses?: ICourse[];
    higher_qualifications?: IQualification[];
    university?: IUniversityDetails;
  };
}

function applyUniversitySettingsToStore(
  response: UniversitySettingsResponse | null,
  dispatch: AppDispatch,
  setLeaddropPopup: (v: IUniversitySettings | null) => void,
) {
  // Must not bail when `settings` is missing: lead_drop_form still needs to hydrate Redux
  // (embedded forms like Blog with lead depend on variant + form payload).
  if (!response?.data) {
    return;
  }

  const {
    lead_drop_popup,
    courses,
    higher_qualifications,
    university,
    lead_drop_form,
  } = response.data;
  const settings = response.data.settings ?? [];

  const isPromotionalBannerEnabled =
    university?.enable_promotional_banner || false;
  dispatch(
    setPromotionalBanner({
      desktop_image: university?.desktop_promotional_banner || "",
      mobile_image: university?.mobile_promotional_banner || "",
      enabled: isPromotionalBannerEnabled,
      animationColor: university?.animation_color || "",
      promotionalIcon: university?.mobile_promotional_icon || "",
      blinkIntervalSeconds:
        university?.promotional_blink_interval_seconds ?? null,
    }),
  );

  dispatch(
    setFloatingIcons({
      whatsApp: {
        whatsAppLink: university?.whatsapp_link || "",
        whatsAppIcon: university?.mobile_whatsapp_icon || "",
      },
      phone: {
        phoneLink: university?.phone_link || "",
        phoneIcon: university?.mobile_phone_icon || "",
      },
    }),
  );

  /* Auto-open only until the user has dismissed it once this session — after
     that the banner is reachable from the floating promo icon only. */
  if (isPromotionalBannerEnabled && !hasSeenPromotionalBanner()) {
    dispatch(setPromotionalBannerOpen(true));
  }

  dispatch(
    setLeaddropFormData({
      formData: lead_drop_form,
      courses: courses || [],
      qualification: higher_qualifications || [],
    } as {
      formData: unknown;
      courses: ICourse[];
      qualification: IQualification[];
    }),
  );

  dispatch(
    setUniversity({
      name: university?.name || "",
      source: university?.source || "",
      uni_code: university?.uni_code || "",
      chatbot_provider: university?.chatbot_provider || "none",
      referral_enabled: university?.referral_enabled === true,
    }),
  );

  dispatch(
    handleSetLeaddropFormData({
      formData: lead_drop_popup as ILeaddropFormData,
      courses: courses || [],
      qualification: (higher_qualifications || []) as IQualification[],
    }),
  );

  const leadPopupSection = String(
    UNIVERSITY_SETTINGS_SECTION_TYPES.LEAD_DROP_POPUP,
  );
  /** Must be the popup row (`section === "1"`), not the first CMS row globally. Otherwise `OpenLeadDrop` maps `variant` to `undefined` and never mounts the dialog. */
  const leaddropPopupSetting =
    settings.find(
      (s) => s.is_active && String(s.section ?? "") === leadPopupSection,
    ) || null;

  setLeaddropPopup(leaddropPopupSetting);
  dispatch(
    setLeadDropPopupUiVariant(leaddropPopupSetting?.variant ?? null),
  );

  const leaddropFormSetting = settings.find(
    (s) => String(s.section) === "2" && Boolean(s.is_active),
  );
  if (leaddropFormSetting?.variant) {
    dispatch(setLeaddropFormVariant(String(leaddropFormSetting.variant)));
  } else if (
    lead_drop_form &&
    typeof lead_drop_form === "object" &&
    lead_drop_form !== null
  ) {
    // Active lead form row exists in API but university_settings row for section "2" is missing/inactive — still render embedded form (default UI variant).
    dispatch(setLeaddropFormVariant("1"));
  }
}

const ClientWrapper = ({
  initialSettings = null,
}: {
  initialSettings?: Awaited<ReturnType<typeof getUniversitySettings>>;
}) => {
  const [leaddropPopup, setLeaddropPopup] =
    useState<IUniversitySettings | null>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let response = initialSettings as UniversitySettingsResponse | null;
      if (!response?.data) {
        response = (await getUniversitySettings()) as UniversitySettingsResponse | null;
      }
      if (cancelled) return;

      /* Transition, not an urgent update: this lands while the page's widget
         chunks are still loading, and it feeds widgets that only need it for
         interactions (embedded lead forms, popup variant, floating icons).
         Urgently re-rendering a widget that hasn't hydrated yet makes React
         throw away its server HTML and show a Suspense fallback — that's the
         "page renders, then a loader, then renders again" reload. In a
         transition React keeps the painted DOM and applies this once ready. */
      startTransition(() => {
        applyUniversitySettingsToStore(response, dispatch, setLeaddropPopup);

        dispatch(
          setIsLeadSubmittedSuccessfully(
            sessionStorage.getItem("isUserVerified") === "true",
          ),
        );
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [initialSettings, dispatch]);

  useDelayForm();

  /**
   * A CMS-authored POPUP form is reason enough to mount the host, even with no legacy
   * popup settings row.
   *
   * Gating solely on `leaddropPopup` meant a university that had only ever configured a
   * custom popup got nothing at all: the row exists to name a legacy design variant, and
   * a custom form does not need one. `OpenLeadDrop` itself decides which of the two to
   * render, so mounting it here is safe in both cases — with neither configured it
   * returns null exactly as before.
   */
  const hasCustomPopup = Boolean(useResolvedLeadForm("popup"));

  if (!leaddropPopup && !hasCustomPopup) return null;
  return <OpenLeadDrop leaddropPopup={leaddropPopup} />;
};

export default ClientWrapper;
